import { useState, useEffect } from "react";
import { CalendarIcon, Clock, TrendingUp, Filter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { getReportSummary, getDailyReport, getCustomers, getTimeEntries } from "@/services/api";
import { format, parseISO, startOfMonth, endOfMonth, startOfWeek, endOfWeek, subMonths } from "date-fns";
import { de } from "date-fns/locale";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  Cell,
} from "recharts";

const DATE_PRESETS = [
  { label: "Diese Woche", value: "this_week" },
  { label: "Dieser Monat", value: "this_month" },
  { label: "Letzter Monat", value: "last_month" },
  { label: "Benutzerdefiniert", value: "custom" },
];

export default function Reports() {
  const [summary, setSummary] = useState([]);
  const [dailyData, setDailyData] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [datePreset, setDatePreset] = useState("this_month");
  const [customDateRange, setCustomDateRange] = useState({
    start: startOfMonth(new Date()),
    end: endOfMonth(new Date()),
  });
  const [selectedCustomer, setSelectedCustomer] = useState("all");
  const [calendarOpen, setCalendarOpen] = useState({ start: false, end: false });

  useEffect(() => {
    loadCustomers();
  }, []);

  useEffect(() => {
    loadReports();
  }, [datePreset, customDateRange, selectedCustomer]);

  const loadCustomers = async () => {
    try {
      const data = await getCustomers();
      setCustomers(data);
    } catch (error) {
      console.error("Error loading customers:", error);
    }
  };

  const getDateRange = () => {
    const today = new Date();
    switch (datePreset) {
      case "this_week":
        return {
          start: format(startOfWeek(today, { weekStartsOn: 1 }), "yyyy-MM-dd"),
          end: format(endOfWeek(today, { weekStartsOn: 1 }), "yyyy-MM-dd"),
        };
      case "this_month":
        return {
          start: format(startOfMonth(today), "yyyy-MM-dd"),
          end: format(endOfMonth(today), "yyyy-MM-dd"),
        };
      case "last_month":
        const lastMonth = subMonths(today, 1);
        return {
          start: format(startOfMonth(lastMonth), "yyyy-MM-dd"),
          end: format(endOfMonth(lastMonth), "yyyy-MM-dd"),
        };
      case "custom":
        return {
          start: format(customDateRange.start, "yyyy-MM-dd"),
          end: format(customDateRange.end, "yyyy-MM-dd"),
        };
      default:
        return {
          start: format(startOfMonth(today), "yyyy-MM-dd"),
          end: format(endOfMonth(today), "yyyy-MM-dd"),
        };
    }
  };

  const loadReports = async () => {
    setLoading(true);
    try {
      const { start, end } = getDateRange();
      const params = { start_date: start, end_date: end };

      if (selectedCustomer !== "all") {
        params.customer_id = selectedCustomer;
      }

      const [summaryData, dailyData] = await Promise.all([
        getReportSummary(params),
        getDailyReport(params),
      ]);

      setSummary(selectedCustomer === "all" ? summaryData : summaryData.filter(s => s.customer_id === selectedCustomer));
      setDailyData(dailyData);
    } catch (error) {
      console.error("Error loading reports:", error);
    } finally {
      setLoading(false);
    }
  };

  const formatDuration = (minutes) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (hours === 0) return `${mins}m`;
    if (mins === 0) return `${hours}h`;
    return `${hours}h ${mins}m`;
  };

  const totalMinutes = summary.reduce((sum, s) => sum + s.total_minutes, 0);
  const totalEntries = summary.reduce((sum, s) => sum + s.entry_count, 0);

  const chartData = dailyData.slice(0, 14).reverse().map((d) => ({
    date: format(parseISO(d.date), "d. MMM", { locale: de }),
    hours: d.total_hours,
    minutes: d.total_minutes,
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-zinc-800 border border-zinc-700 rounded-lg p-3 shadow-lg">
          <p className="text-sm text-zinc-300">{payload[0].payload.date}</p>
          <p className="text-lg font-bold text-amber-500">
            {formatDuration(payload[0].payload.minutes)}
          </p>
        </div>
      );
    }
    return null;
  };

  if (loading && summary.length === 0) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="reports-loading">
        <div className="animate-pulse text-zinc-500">Laden...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in-up" data-testid="reports-page">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-zinc-50" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Berichte
        </h1>
        <p className="text-sm text-zinc-500 mt-1">
          Übersicht deiner Arbeitszeiten
        </p>
      </div>

      {/* Filters */}
      <Card className="bg-zinc-900/50 border-zinc-800">
        <CardContent className="p-4 space-y-4">
          <div className="flex items-center gap-2 text-zinc-400 text-sm">
            <Filter size={16} />
            <span>Filter</span>
          </div>
          
          <div className="grid grid-cols-2 gap-3">
            {/* Date Preset */}
            <Select value={datePreset} onValueChange={setDatePreset}>
              <SelectTrigger className="h-10 bg-zinc-950 border-zinc-800" data-testid="date-preset-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                {DATE_PRESETS.map((preset) => (
                  <SelectItem key={preset.value} value={preset.value} className="focus:bg-zinc-800">
                    {preset.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            {/* Customer Filter */}
            <Select value={selectedCustomer} onValueChange={setSelectedCustomer}>
              <SelectTrigger className="h-10 bg-zinc-950 border-zinc-800" data-testid="customer-filter-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-zinc-900 border-zinc-800">
                <SelectItem value="all" className="focus:bg-zinc-800">
                  Alle Kunden
                </SelectItem>
                {customers.map((customer) => (
                  <SelectItem key={customer.id} value={customer.id} className="focus:bg-zinc-800">
                    <div className="flex items-center gap-2">
                      <div
                        className="customer-dot"
                        style={{ backgroundColor: customer.color }}
                      />
                      {customer.name}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Custom Date Range */}
          {datePreset === "custom" && (
            <div className="grid grid-cols-2 gap-3">
              <Popover open={calendarOpen.start} onOpenChange={(open) => setCalendarOpen(prev => ({ ...prev, start: open }))}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-10 justify-start text-left font-normal bg-zinc-950 border-zinc-800 text-sm"
                    data-testid="start-date-picker"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-zinc-500" />
                    {format(customDateRange.start, "d. MMM", { locale: de })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="start">
                  <Calendar
                    mode="single"
                    selected={customDateRange.start}
                    onSelect={(date) => {
                      if (date) {
                        setCustomDateRange(prev => ({ ...prev, start: date }));
                        setCalendarOpen(prev => ({ ...prev, start: false }));
                      }
                    }}
                    locale={de}
                  />
                </PopoverContent>
              </Popover>

              <Popover open={calendarOpen.end} onOpenChange={(open) => setCalendarOpen(prev => ({ ...prev, end: open }))}>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className="h-10 justify-start text-left font-normal bg-zinc-950 border-zinc-800 text-sm"
                    data-testid="end-date-picker"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4 text-zinc-500" />
                    {format(customDateRange.end, "d. MMM", { locale: de })}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0 bg-zinc-900 border-zinc-800" align="start">
                  <Calendar
                    mode="single"
                    selected={customDateRange.end}
                    onSelect={(date) => {
                      if (date) {
                        setCustomDateRange(prev => ({ ...prev, end: date }));
                        setCalendarOpen(prev => ({ ...prev, end: false }));
                      }
                    }}
                    locale={de}
                  />
                </PopoverContent>
              </Popover>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 gap-3">
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-2">
            <Clock size={16} className="text-amber-500" />
            <span className="text-xs text-zinc-500 uppercase tracking-wide">Gesamt</span>
          </div>
          <p className="text-2xl font-bold time-display" data-testid="total-time">
            {formatDuration(totalMinutes)}
          </p>
          <p className="text-xs text-zinc-500 mt-1">
            {Math.round(totalMinutes / 60 * 10) / 10} Stunden
          </p>
        </div>
        <div className="stat-card">
          <div className="flex items-center gap-2 mb-2">
            <TrendingUp size={16} className="text-emerald-500" />
            <span className="text-xs text-zinc-500 uppercase tracking-wide">Einträge</span>
          </div>
          <p className="text-2xl font-bold" data-testid="total-entries">{totalEntries}</p>
          <p className="text-xs text-zinc-500 mt-1">
            Zeiteinträge
          </p>
        </div>
      </div>

      {/* Daily Chart */}
      {chartData.length > 0 && (
        <Card className="bg-zinc-900/50 border-zinc-800">
          <CardContent className="p-4">
            <h3 className="text-sm font-medium text-zinc-400 mb-4">Tägliche Übersicht</h3>
            <div className="h-48" data-testid="daily-chart">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} barCategoryGap="20%">
                  <XAxis
                    dataKey="date"
                    tick={{ fill: "#71717A", fontSize: 10 }}
                    tickLine={false}
                    axisLine={{ stroke: "#27272A" }}
                  />
                  <YAxis
                    tick={{ fill: "#71717A", fontSize: 10 }}
                    tickLine={false}
                    axisLine={false}
                    tickFormatter={(value) => `${value}h`}
                  />
                  <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255,255,255,0.05)" }} />
                  <Bar dataKey="hours" radius={[4, 4, 0, 0]}>
                    {chartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill="#F59E0B" />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Customer Summary */}
      <div>
        <h3 className="text-lg font-semibold text-zinc-50 mb-4" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Nach Kunden
        </h3>
        {summary.length === 0 ? (
          <div className="empty-state" data-testid="empty-summary">
            <Clock size={48} className="text-zinc-700 mb-4" />
            <p className="text-zinc-500">Keine Daten für diesen Zeitraum</p>
          </div>
        ) : (
          <div className="space-y-3" data-testid="customer-summary">
            {summary.map((item) => {
              const percentage = totalMinutes > 0 ? (item.total_minutes / totalMinutes) * 100 : 0;
              return (
                <Card
                  key={item.customer_id}
                  className="bg-zinc-900/50 border-zinc-800"
                  data-testid={`summary-${item.customer_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div
                        className="w-10 h-10 rounded-lg flex items-center justify-center text-sm font-bold"
                        style={{ backgroundColor: `${item.customer_color}20`, color: item.customer_color }}
                      >
                        {item.customer_name.charAt(0).toUpperCase()}
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-zinc-100">{item.customer_name}</p>
                        <p className="text-xs text-zinc-500">{item.entry_count} Einträge</p>
                      </div>
                      <div className="text-right">
                        <p className="font-bold text-zinc-100 time-display">
                          {formatDuration(item.total_minutes)}
                        </p>
                        <p className="text-xs text-zinc-500">{item.total_hours}h</p>
                      </div>
                    </div>
                    <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full transition-all duration-500"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: item.customer_color,
                        }}
                      />
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
